import { EntityId } from "@reduxjs/toolkit"
import { useCallback } from "react"
import { useDropzone } from "react-dropzone"
import { UPLOAD_ACCEPTED_FILE_TYPES } from "~/data/const"
import { useCurrentPageId } from "~/hooks/useCurrentPageId"
import { useImageRegion, useImageRegionActions } from "~/hooks/useImageRegion"
import { useLineItemActions } from "~/hooks/useLineItem"
import { useNextActionableBlock } from "~/hooks/useNextActionableBlock"
import { useProcessActions } from "~/hooks/useProcessActions"
import { scrollTo, wait } from "~/services/Utils"

interface Options {
  lineItemId?: EntityId | null
  imageRegionId?: EntityId | null
  onStartImporting: () => void
  onCompleteImporting: () => void
}

export const useImagePicker = ({
  imageRegionId,
  lineItemId,
  onStartImporting,
  onCompleteImporting,
}: Options) => {
  const process = useProcessActions()
  const pageId = useCurrentPageId()
  const { updateMissingImages } = useLineItemActions()
  const { nextBlockType } = useNextActionableBlock("image-upload")
  const { imageRegion } = useImageRegion(imageRegionId!)
  const { updateImage } = useImageRegionActions()

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onStartImporting()

        if (lineItemId && imageRegionId) {
          if (imageRegion?.uploadId) {
            const firstImage = acceptedFiles[0]
            updateImage(imageRegionId, firstImage)
          } else {
            updateMissingImages(lineItemId, acceptedFiles, imageRegionId)
          }

          await wait(1000)
        } else if (lineItemId) {
          updateMissingImages(lineItemId, acceptedFiles)
        } else {
          const firstImage = acceptedFiles[0]
          process.createUpload({
            pageId,
            file: firstImage,
            scrollTo,
            nextBlockName: nextBlockType,
          })

          const remainingImages = acceptedFiles.slice(1)

          remainingImages.forEach((file: File) =>
            process.createUpload({
              pageId,
              file,
            })
          )
        }
      }
      onCompleteImporting()
    },
    [
      onCompleteImporting,
      onStartImporting,
      lineItemId,
      imageRegionId,
      imageRegion?.uploadId,
      updateImage,
      updateMissingImages,
      process,
      pageId,
      nextBlockType,
    ]
  )

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: UPLOAD_ACCEPTED_FILE_TYPES,
  })

  return {
    getRootProps,
    getInputProps,
    openImagePicker: open,
  }
}
